import React from 'react';
import { get, size } from 'lodash';
import styled from 'styled-components';

import Avatar from '../../Clients/Avatar';
import Button from '../../../Button';
import CallIcon from '../../../CallIcon';
import Dropdown, {
  StyledContent as StyledDropdownContent,
} from '../../../Dropdown';
import Duration from '../../../Duration';
import Name from '../../Clients/Name';
import OrderStatus from '../../Orders/OrderStatus';
import TicketType from '../../Tickets/TicketType';
import TimeFrom from '../../Common/TimeFrom';
import Timer from '../../../Timer';

import { Record, Listen, AlfaPartner } from '@icons/communication';

import { getCallDuration } from '../../../../utils/date';
import { pluralize } from '../../../../utils/userflow';
import { ICallCard, ReasonType } from './types';

const reasonText = ['причина', 'причины', 'причины'];

const Reason = ({ reason }: { reason: ReasonType }) => {
  const { description, type } = reason;

  return (
    <StyledReason>
      <StyledReasonType title={type}>{type}</StyledReasonType>
      {description && (
        <StyledReasonsDescription title={description}>
          {description}
        </StyledReasonsDescription>
      )}
    </StyledReason>
  );
};

const reasonsContentRenderer = (reasons: ReasonType[]) =>
  reasons.map((reason: ReasonType) => (
    <Reason key={reason.id} reason={reason} />
  ));

const reasonsHeadRenderer = (reasons: ReasonType[]) => {
  const hiddenReasonsAmount = size(reasons) - 1;

  return (
    <StyledReasons>
      <Reason reason={reasons[0]} />
      <StyledReasonsMore>
        {`Еще ${hiddenReasonsAmount} ${pluralize(
          hiddenReasonsAmount,
          reasonText
        )}`}
      </StyledReasonsMore>
    </StyledReasons>
  );
};

const CallCard = ({
  call,
  onListen,
  contact,
  operator,
  order,
  ticket,
  ...props
}: ICallCard) => {
  const {
    answeredAt,
    callUrl,
    finishedAt,
    isAnswered,
    isCallback,
    isIncoming,
    isListen,
    isListenable,
    pbxId,
    reasons,
    recordUrl,
  } = call;
  const contactAvatarUrl = get(contact, 'avatarUrl');
  const contactName = get(contact, 'fullName');
  const contactPartner = get(contact, 'partner');
  const operatorName = get(operator, 'name');
  const operatorNumber = get(operator, 'number');
  const orderHref = get(order, 'href');
  const orderId = get(order, 'id');
  const orderStatus = get(order, 'status');
  const ticketSubtype = get(ticket, 'subtype');
  const ticketType = get(ticket, 'type');

  const reasonsAmount = size(reasons);
  const singleReason = reasonsAmount === 1 ? reasons[0] : null;

  const duration = getCallDuration(answeredAt, finishedAt);
  const isActive = !finishedAt;

  const handleListen = event => {
    event.stopPropagation();
    const { pbxId } = call;
    onListen?.({ pbxId });
  };

  return (
    <StyledCallCard {...props}>
      <StyledMainRow>
        <StyledAvatar
          name={contactName}
          pulsing={isActive}
          url={contactAvatarUrl}
        />
        <StyledDescription>
          {contactName && <StyledContactName name={contactName} />}
          <StyledCall>
            {contactPartner === 'alfa' && <StyledAlfaPartner />}
            <StyledCallIcon
              href={callUrl}
              isActive={isActive}
              isAnswered={isAnswered}
              isCallback={isCallback}
              isIncoming={isIncoming}
            />
            {isActive && duration >= 0 && (
              <StyledTimer initialSeconds={duration} />
            )}
            {!isActive && <StyledDuration duration={duration} />}
            {recordUrl && (
              <StyledRecordLink
                href={recordUrl}
                target="_blank"
                title="Прослушать запись"
              >
                <StyledRecordIcon />
              </StyledRecordLink>
            )}
            {!isActive && finishedAt && (
              <StyledTimeFrom full time={finishedAt} />
            )}
          </StyledCall>
          {operatorName && (
            <StyledOperator>
              <StyledOperatorName>{operatorName}</StyledOperatorName>
              {operatorNumber && (
                <StyledOperatorNumber>{operatorNumber}</StyledOperatorNumber>
              )}
            </StyledOperator>
          )}
        </StyledDescription>
        {pbxId && isListenable && isActive && (
          <StyledListenButton
            active={isListen}
            functional
            onClick={handleListen}
          >
            <StyledListenIcon />
          </StyledListenButton>
        )}
      </StyledMainRow>
      {ticketType && (
        <StyledTicketType subtype={ticketSubtype} type={ticketType} />
      )}
      {orderId && orderHref && orderStatus && (
        <StyledOrderStatus href={orderHref} id={orderId} status={orderStatus} />
      )}
      {reasonsAmount === 1 && singleReason && <Reason reason={singleReason} />}
      {reasonsAmount > 1 && (
        <StyledReasonsDropdown
          contentRenderer={reasonsContentRenderer(reasons)}
          headRenderer={reasonsHeadRenderer(reasons)}
          onToggle={() => null}
        />
      )}
    </StyledCallCard>
  );
};

const StyledCallCard = styled.div`
  border: 1px solid #919499;
  border-radius: 4px;
  box-sizing: border-box;
  color: #17181a;
  display: flex;
  flex-direction: column;
  height: 133px;
  justify-content: space-between;
  padding: 2px 10px 7px;
  position: relative;
  width: 270px;
`;

const StyledMainRow = styled.div`
  align-items: center;
  display: flex;
  height: 53px;
  justify-content: space-between;
`;

const StyledAvatar = styled(Avatar)`
  flex-shrink: 0;
  margin-right: 10px;
  margin-top: 3px;
  z-index: 1;
`;

const StyledDescription = styled.div`
  flex-grow: 1;
  overflow: hidden;
`;

const StyledContactName = styled(Name)`
  font-size: 14px;
  font-weight: bold;
`;

const StyledCall = styled.div`
  align-items: center;
  color: #4d4d4d;
  display: flex;
  height: 21px;
`;

const StyledAlfaPartner = styled(AlfaPartner)`
  height: 17px;
  margin-right: 4px;
  width: 17px;
`;

const StyledCallIcon = styled(CallIcon)`
  height: 17px;
  margin-right: 4px;
  width: 17px;
`;

const StyledTimer = styled(Timer)`
  flex-shrink: 0;
  font-size: 12px;
  font-weight: normal;
  margin-right: 6px;
  min-width: 31px;
`;

const StyledDuration = styled(Duration)`
  flex-shrink: 0;
  margin-right: 6px;
  min-width: 31px;
`;

const StyledRecordLink = styled.a`
  flex-shrink: 0;
`;

const StyledRecordIcon = styled(Record)`
  cursor: pointer;
  height: 16px;
  margin-right: 7px;
  margin-top: 4px;
  width: 16px;
`;

const StyledTimeFrom = styled(TimeFrom)`
  flex-grow: 1;
  white-space: nowrap;
`;

const StyledOperator = styled.div`
  color: #4d4d4d;
  display: flex;
  font-size: 12px;
`;

const StyledOperatorName = styled.div``;

const StyledOperatorNumber = styled.div`
  ${StyledOperatorName} + & {
    :before {
      content: '\u22C5';
      margin: 0 5px;
    }
  }
`;

const StyledListenButton = styled(Button)`
  flex-shrink: 0;
  height: 17px;
  position: relative;
  width: 15px;
  z-index: 1;

  ${({ active }) =>
    active &&
    `
    svg path {
      stroke: white;
    }

    &:after,
    &:before {
      animation: pulsing 1.5s infinite ease-in-out;
      background-image: linear-gradient(to top, #00e04b, #00b0b6);
      border-radius: 100%;
      content: '';
      left: 50%;
      position: absolute;
      top: 50%;
      z-index: -1;
    }
    
    &:after {
      height: 22px;
      margin-left: -12px;
      margin-top: -11px;
      opacity: 1;
      width: 22px;
    }

    &:before {
      height: 32px;
      margin-left: -17px;
      margin-top: -16px;
      opacity: 0.3;
      width: 32px;
    }

    @keyframes pulsing {
      from { transform: scale(.8); }
    }
  `}
`;

const StyledListenIcon = styled(Listen)`
  width: 15px;
  height: 17px;
`;

const StyledTicketType = styled(TicketType)`
  width: min-content;
`;

const StyledOrderStatus = styled(OrderStatus)`
  font-size: 12px;
  height: 18px;
  line-height: 18px;
  width: min-content;
`;

const StyledReason = styled.div`
  color: #919499;
  display: flex;
  font-size: 12px;
  overflow: hidden;
  text-align: left;

  > * {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const StyledReasonType = styled.div`
  color: black;
  font-weight: 700;
`;

const StyledReasonsDescription = styled.div`
  ${StyledReasonType} + & {
    :before {
      content: '\u22C5';
      margin: 0 5px;
    }
  }
`;

const StyledReasonsDropdown = styled(Dropdown)`
  background: none;
  border: none;
  font-family: inherit;
  position: relative;

  > button {
    outline: none;
  }

  ${StyledDropdownContent} {
    flex-direction: column;
    margin-top: 0;
    padding: 5px 10.5px;
    top: -5px;
    width: calc(100% + 22px);

    :before {
      display: none;
    }

    ${StyledReason} {
      padding-top: 5px;
      white-space: normal;

      &:first-child {
        padding: 0;
      }
    }
  }
`;

const StyledReasons = styled.div`
  display: flex;
  font-size: 12px;
  width: 100%;
`;

const StyledReasonsMore = styled.span`
  margin-left: 10px;
  white-space: nowrap;
`;

export default CallCard;
